import React from 'react';
import { WifiOff, RefreshCw } from 'lucide-react';

/**
 * ChunkErrorBoundary
 * ----------------------------------------------------------------
 * Catches `ChunkLoadError` / "Loading chunk N failed" errors that
 * originate from `React.lazy(() => import(...))` when the browser
 * is offline (or the CDN edge is unreachable) at the moment the
 * route JavaScript would have been fetched.
 *
 * Two-layer detection:
 *  1. React error-boundary lifecycle — catches errors thrown inside
 *     the render tree (this is where lazy-import rejections surface).
 *  2. `window.unhandledrejection` listener — catches chunk-load
 *     promise rejections that fire before the Suspense boundary can
 *     hand them to React (e.g. during router transitions).
 *
 * The fallback UI matches the app's design language and offers a
 * one-click "Reload Page" recovery. The Reload button is disabled
 * while the browser is still offline so users can't reload into a
 * second failure.
 */
const CHUNK_RE = /(Loading chunk \d+ failed|Loading CSS chunk|ChunkLoadError|Failed to fetch dynamically imported module|Importing a module script failed)/i;

function isChunkError(err) {
  if (!err) return false;
  const name = err.name || err.constructor?.name || '';
  const msg  = err.message || String(err);
  return name === 'ChunkLoadError' || CHUNK_RE.test(msg);
}

export default class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasChunkError: false,
      online: typeof navigator === 'undefined' ? true : navigator.onLine,
    };
  }

  static getDerivedStateFromError(error) {
    if (isChunkError(error)) return { hasChunkError: true };
    // Not a chunk load error — let it propagate to the next boundary.
    return null;
  }

  componentDidCatch(error, info) {
    // Console log for the developer; do not rethrow non-chunk errors here
    // because they've already been swallowed by getDerivedStateFromError
    // returning null (which triggers a re-throw by React internally).
    if (isChunkError(error)) {
      // eslint-disable-next-line no-console
      console.warn('[ChunkErrorBoundary]', error?.message || error, info);
    }
  }

  componentDidMount() {
    this._onOnline  = () => this.setState({ online: true });
    this._onOffline = () => this.setState({ online: false });
    window.addEventListener('online',  this._onOnline);
    window.addEventListener('offline', this._onOffline);

    // Catch chunk-load rejections that never reach the React tree
    // (e.g. cross-boundary router navigations while offline).
    this._onRejection = (e) => {
      const reason = e?.reason;
      if (isChunkError(reason)) {
        e.preventDefault?.();
        this.setState({ hasChunkError: true });
      }
    };
    window.addEventListener('unhandledrejection', this._onRejection);
  }

  componentWillUnmount() {
    window.removeEventListener('online',  this._onOnline);
    window.removeEventListener('offline', this._onOffline);
    window.removeEventListener('unhandledrejection', this._onRejection);
  }

  handleReload = () => {
    // Force a fresh reload so the browser bypasses the stale chunk
    // reference in its module cache.
    window.location.reload();
  };

  render() {
    if (!this.state.hasChunkError) return this.props.children;

    const { online } = this.state;
    return (
      <div
        data-testid="chunk-error-fallback"
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-background/95 p-6 backdrop-blur-sm"
      >
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-lg md:p-8">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-950/60 dark:text-amber-400">
              <WifiOff className="h-5 w-5" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <h2
                data-testid="chunk-error-title"
                className="text-lg font-semibold text-foreground"
              >
                Module could not be loaded
              </h2>
              <p
                data-testid="chunk-error-message"
                className="mt-2 text-sm text-muted-foreground"
              >
                Internet connection is required to load this module. Please
                check your connection and try again.
              </p>
              <p
                data-testid="chunk-error-status"
                className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ${
                  online
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-400'
                    : 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-400'
                }`}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    online ? 'bg-emerald-500' : 'bg-amber-500 animate-pulse'
                  }`}
                />
                {online ? 'Connection restored' : 'Still offline — waiting for network'}
              </p>
            </div>
          </div>
          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              data-testid="chunk-error-reload"
              onClick={this.handleReload}
              disabled={!online}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw className="h-4 w-4" aria-hidden />
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }
}
